// Suppliers + city coordinates + district coordinates for the interactive drill-down map.
// Three-level hierarchy: Country → City (GREEN markers) → District (WHITE markers) → Suppliers list.
//
// Architecture:
//   1. TR — all 81 provinces have curated suppliers (1–5 firms per province) with real-sounding
//      insulation / yapı brand names and TR-formatted phone numbers.
//   2. Global — every (country, city) pair listed in CITY_COORDS is auto-populated with 3
//      brand suppliers so the sidebar ALWAYS shows Name + Phone when a green marker is clicked.

import { COUNTRY_CITIES } from "@/lib/locations";

// ─────────────────────────────────────────────────────────────
// TURKEY — full 81-province curated supplier dataset
// Keys use Turkish characters (matches locations.js TR_PROVINCES).
// ─────────────────────────────────────────────────────────────
const TR_SUPPLIERS_FLAT = {
  "İstanbul": [
    ["İzocam A.Ş.",            "+90 262 754 24 00"],
    ["Rockwool Türkiye",       "+90 216 581 00 00"],
    ["Knauf İstanbul",         "+90 212 325 40 00"],
    ["Saint-Gobain Türkiye",   "+90 216 578 62 00"],
    ["Betek Boya",             "+90 216 581 10 00"],
  ],
  "Ankara": [
    ["Dow Türkiye",            "+90 312 457 10 00"],
    ["BASF Türkiye",           "+90 312 409 40 00"],
    ["Ankara Yalıtım",         "+90 312 354 22 00"],
    ["Başkent İzolasyon",      "+90 312 285 60 00"],
  ],
  "İzmir": [
    ["Knauf İzmir",            "+90 232 472 88 00"],
    ["Hekim Yapı İzmir",       "+90 232 877 04 44"],
    ["Ege Yalıtım",            "+90 232 458 30 00"],
    ["İzmir İzolasyon Merkezi","+90 232 375 12 00"],
  ],
  "Bursa": [
    ["Hekim Yapı Bursa",       "+90 224 280 10 00"],
    ["Yalıtım Market Bursa",   "+90 224 452 33 00"],
    ["Bursa Strafor",          "+90 224 261 08 00"],
  ],
  "Antalya": [
    ["Antalya İzolasyon",      "+90 242 312 50 00"],
    ["Akdeniz Yalıtım",        "+90 242 228 14 00"],
    ["Güney Yapı Market",      "+90 242 345 67 89"],
  ],
  "Adana": [
    ["Adana Yalıtım Sanayi",   "+90 322 441 20 00"],
    ["Çukurova İzolasyon",     "+90 322 352 15 00"],
    ["Temsa İnşaat",           "+90 322 428 10 00"],
  ],
  "Gaziantep": [
    ["Gaziantep İzolasyon",    "+90 342 338 20 00"],
    ["Güneydoğu Yalıtım",      "+90 342 215 80 00"],
    ["Antep Yapı Market",      "+90 342 322 45 00"],
  ],
  "Konya": [
    ["Konya İzolasyon",        "+90 332 236 10 00"],
    ["Selçuklu Yalıtım",       "+90 332 241 55 00"],
    ["İç Anadolu Yapı",        "+90 332 265 30 00"],
  ],
  "Kayseri": [
    ["Kayseri Yalıtım",        "+90 352 336 80 00"],
    ["Erciyes İzolasyon",      "+90 352 321 42 00"],
  ],
  "Mersin": [
    ["Mersin Yalıtım",         "+90 324 237 50 00"],
    ["Akdeniz İzolasyon Mersin","+90 324 328 14 00"],
  ],
  "Eskişehir": [
    ["Eskişehir İzolasyon",    "+90 222 234 50 00"],
    ["Batı Anadolu Yalıtım",   "+90 222 221 35 00"],
  ],
  "Diyarbakır": [
    ["Diyarbakır Yalıtım",     "+90 412 228 60 00"],
    ["Dicle İzolasyon",        "+90 412 235 40 00"],
  ],
  "Samsun": [
    ["Samsun İzolasyon",       "+90 362 431 20 00"],
    ["Karadeniz Yalıtım",      "+90 362 445 35 00"],
  ],
  "Trabzon": [
    ["Trabzon Yalıtım",        "+90 462 326 10 00"],
    ["Doğu Karadeniz İzolasyon","+90 462 321 55 00"],
  ],
  "Denizli": [
    ["Denizli İzolasyon",      "+90 258 264 30 00"],
    ["Pamukkale Yalıtım",      "+90 258 271 80 00"],
  ],
  "Malatya": [
    ["Malatya Yalıtım",        "+90 422 323 10 00"],
    ["Doğu Anadolu İzolasyon", "+90 422 336 45 00"],
  ],
  "Erzurum": [
    ["Erzurum İzolasyon",      "+90 442 234 60 00"],
    ["Dadaş Yalıtım",          "+90 442 218 35 00"],
  ],
  "Sivas": [
    ["Sivas Yalıtım",          "+90 346 221 50 00"],
    ["Kızılırmak İzolasyon",   "+90 346 225 30 00"],
  ],
  "Manisa": [
    ["Manisa İzolasyon",       "+90 236 231 40 00"],
    ["Gediz Yalıtım",          "+90 236 238 20 00"],
  ],
  "Kocaeli": [
    ["İzmit Yalıtım Sanayi",   "+90 262 335 60 00"],
    ["Gebze İzolasyon",        "+90 262 641 80 00"],
    ["Kocaeli Yapı Market",    "+90 262 325 15 00"],
  ],
  "Sakarya": [
    ["Sakarya İzolasyon",      "+90 264 275 30 00"],
    ["Adapazarı Yalıtım",      "+90 264 282 10 00"],
  ],
  "Tekirdağ": [
    ["Tekirdağ Yalıtım",       "+90 282 261 40 00"],
    ["Trakya İzolasyon",       "+90 282 263 55 00"],
  ],
  "Balıkesir": [
    ["Balıkesir İzolasyon",    "+90 266 241 20 00"],
    ["Marmara Yalıtım",        "+90 266 245 35 00"],
  ],
  "Hatay": [
    ["Hatay İzolasyon",        "+90 326 214 60 00"],
    ["Antakya Yalıtım",        "+90 326 221 30 00"],
  ],
  "Kahramanmaraş": [
    ["Maraş İzolasyon",        "+90 344 223 50 00"],
    ["Kahramanmaraş Yalıtım",  "+90 344 231 20 00"],
  ],
  "Van": [
    ["Van İzolasyon",          "+90 432 215 40 00"],
    ["Doğu Yalıtım Van",       "+90 432 212 60 00"],
  ],
  "Aydın": [
    ["Aydın İzolasyon",        "+90 256 213 30 00"],
    ["Menderes Yalıtım",       "+90 256 225 10 00"],
  ],
  "Muğla": [
    ["Muğla İzolasyon",        "+90 252 214 50 00"],
    ["Bodrum Yalıtım",         "+90 252 316 20 00"],
  ],
  "Şanlıurfa": [
    ["Urfa İzolasyon",         "+90 414 313 40 00"],
    ["Güneydoğu Yapı Urfa",    "+90 414 315 60 00"],
  ],
  "Elazığ": [
    ["Elazığ İzolasyon",       "+90 424 218 30 00"],
    ["Fırat Yalıtım",          "+90 424 212 55 00"],
  ],
  "Afyonkarahisar": [
    ["Afyon İzolasyon",        "+90 272 213 20 00"],
    ["Afyon Yapı Market",      "+90 272 215 40 00"],
  ],
  "Ordu": [
    ["Ordu Yalıtım",           "+90 452 214 30 00"],
    ["Karadeniz Yapı Ordu",    "+90 452 225 10 00"],
  ],
  "Tokat": [
    ["Tokat İzolasyon",        "+90 356 212 50 00"],
    ["Yeşilırmak Yalıtım",     "+90 356 214 30 00"],
  ],
  "Edirne": [
    ["Edirne İzolasyon",       "+90 284 212 40 00"],
    ["Trakya Yapı Edirne",     "+90 284 225 20 00"],
  ],
  "Zonguldak": [
    ["Zonguldak Yalıtım",      "+90 372 251 30 00"],
    ["Batı Karadeniz İzolasyon","+90 372 253 10 00"],
  ],
  "Çanakkale": [
    ["Çanakkale İzolasyon",    "+90 286 213 40 00"],
    ["Boğaz Yalıtım",          "+90 286 217 20 00"],
  ],
  "Rize": [
    ["Rize Yalıtım",           "+90 464 213 50 00"],
    ["Çay İzolasyon",          "+90 464 214 30 00"],
  ],
  "Yozgat":         [["Yozgat İzolasyon",     "+90 354 212 40 00"]],
  "Aksaray":        [["Aksaray Yalıtım",      "+90 382 213 30 00"]],
  "Kastamonu":      [["Kastamonu İzolasyon",  "+90 366 212 50 00"]],
  "Düzce":          [["Düzce Yalıtım",        "+90 380 512 30 00"]],
  "Giresun":        [["Giresun İzolasyon",    "+90 454 212 40 00"]],
  "Bolu":           [["Bolu Yalıtım",         "+90 374 215 30 00"]],
  "Isparta": [
    ["Isparta İzolasyon",      "+90 246 218 40 00"],
    ["Göller Bölgesi Yalıtım", "+90 246 232 10 00"],
  ],
  "Çorum":          [["Çorum Yalıtım",        "+90 364 213 50 00"]],
  "Amasya":         [["Amasya İzolasyon",     "+90 358 212 40 00"]],
  "Kırklareli":     [["Kırklareli Yalıtım",   "+90 288 212 30 00"]],
  "Kütahya":        [["Kütahya İzolasyon",    "+90 274 223 40 00"]],
  "Osmaniye":       [["Osmaniye Yalıtım",     "+90 328 813 50 00"]],
  "Mardin":         [["Mardin İzolasyon",     "+90 482 212 30 00"]],
  "Batman":         [["Batman Yalıtım",       "+90 488 213 40 00"]],
  "Siirt":          [["Siirt İzolasyon",      "+90 484 223 30 00"]],
  "Muş":            [["Muş Yalıtım",          "+90 436 212 50 00"]],
  "Bitlis":         [["Bitlis İzolasyon",     "+90 434 226 30 00"]],
  "Ağrı":           [["Ağrı Yalıtım",         "+90 472 215 40 00"]],
  "Kars":           [["Kars İzolasyon",       "+90 474 212 30 00"]],
  "Iğdır":          [["Iğdır Yalıtım",        "+90 476 227 40 00"]],
  "Ardahan":        [["Ardahan İzolasyon",    "+90 478 211 30 00"]],
  "Artvin":         [["Artvin Yalıtım",       "+90 466 212 40 00"]],
  "Bayburt":        [["Bayburt İzolasyon",    "+90 458 211 30 00"]],
  "Gümüşhane":      [["Gümüşhane Yalıtım",    "+90 456 213 40 00"]],
  "Hakkari":        [["Hakkari İzolasyon",    "+90 438 211 30 00"]],
  "Şırnak":         [["Şırnak Yalıtım",       "+90 486 216 40 00"]],
  "Tunceli":        [["Tunceli İzolasyon",    "+90 428 212 30 00"]],
  "Bingöl":         [["Bingöl Yalıtım",       "+90 426 213 40 00"]],
  "Niğde":          [["Niğde İzolasyon",      "+90 388 232 30 00"]],
  "Nevşehir": [
    ["Nevşehir Yalıtım",       "+90 384 213 40 00"],
    ["Kapadokya Yapı",         "+90 384 215 20 00"],
  ],
  "Kırşehir":       [["Kırşehir İzolasyon",   "+90 386 212 30 00"]],
  "Karaman":        [["Karaman Yalıtım",      "+90 338 212 40 00"]],
  "Bilecik":        [["Bilecik İzolasyon",    "+90 228 212 30 00"]],
  "Bartın":         [["Bartın Yalıtım",       "+90 378 227 40 00"]],
  "Karabük":        [["Karabük İzolasyon",    "+90 370 412 30 00"]],
  "Sinop":          [["Sinop Yalıtım",        "+90 368 261 40 00"]],
  "Çankırı":        [["Çankırı İzolasyon",    "+90 376 213 30 00"]],
  "Uşak":           [["Uşak Yalıtım",         "+90 276 212 40 00"]],
  "Burdur":         [["Burdur İzolasyon",     "+90 248 233 30 00"]],
  "Kırıkkale":      [["Kırıkkale Yalıtım",    "+90 318 224 40 00"]],
  "Erzincan":       [["Erzincan İzolasyon",   "+90 446 214 30 00"]],
  "Adıyaman":       [["Adıyaman Yalıtım",     "+90 416 216 40 00"]],
  "Yalova":         [["Yalova İzolasyon",     "+90 226 812 30 00"]],
  "Kilis":          [["Kilis Yalıtım",        "+90 348 813 30 00"]],
};

// Turkish provinces → (lat, lon). Covers every key in TR_SUPPLIERS_FLAT.
const TR_CITY_COORDS = {
  "İstanbul":[41.0082,28.9784], "Ankara":[39.9334,32.8597], "İzmir":[38.4192,27.1287],
  "Bursa":[40.1885,29.0610], "Antalya":[36.8969,30.7133], "Adana":[37.0000,35.3213],
  "Gaziantep":[37.0662,37.3833], "Konya":[37.8746,32.4932], "Kayseri":[38.7312,35.4787],
  "Mersin":[36.8121,34.6415], "Eskişehir":[39.7767,30.5206], "Diyarbakır":[37.9144,40.2306],
  "Samsun":[41.2867,36.3300], "Trabzon":[41.0027,39.7168], "Denizli":[37.7765,29.0864],
  "Malatya":[38.3552,38.3095], "Erzurum":[39.9055,41.2658], "Sivas":[39.7477,37.0179],
  "Manisa":[38.6191,27.4289], "Kocaeli":[40.8533,29.8815], "Sakarya":[40.6940,30.4358],
  "Tekirdağ":[41.2824,27.5120], "Balıkesir":[39.6484,27.8826], "Hatay":[36.4018,36.3498],
  "Kahramanmaraş":[37.5858,36.9371], "Van":[38.4891,43.4089], "Aydın":[37.8560,27.8416],
  "Muğla":[37.2153,28.3636], "Şanlıurfa":[37.1591,38.7969], "Elazığ":[38.6810,39.2264],
  "Afyonkarahisar":[38.7507,30.5567], "Ordu":[40.9839,37.8764], "Tokat":[40.3167,36.5544],
  "Edirne":[41.6818,26.5623], "Zonguldak":[41.4564,31.7987], "Çanakkale":[40.1553,26.4142],
  "Rize":[41.0201,40.5234], "Yozgat":[39.8181,34.8147], "Aksaray":[38.3687,34.0370],
  "Kastamonu":[41.3887,33.7827], "Düzce":[40.8438,31.1565], "Giresun":[40.9128,38.3895],
  "Bolu":[40.7360,31.6113], "Isparta":[37.7648,30.5566], "Çorum":[40.5506,34.9556],
  "Amasya":[40.6499,35.8353], "Kırklareli":[41.7351,27.2252], "Kütahya":[39.4168,29.9833],
  "Osmaniye":[37.0746,36.2464], "Mardin":[37.3212,40.7245], "Batman":[37.8812,41.1351],
  "Siirt":[37.9333,41.9500], "Muş":[38.9462,41.7539], "Bitlis":[38.4010,42.1095],
  "Ağrı":[39.7191,43.0503], "Kars":[40.6013,43.0975], "Iğdır":[39.9167,44.0500],
  "Ardahan":[41.1105,42.7022], "Artvin":[41.1828,41.8183], "Bayburt":[40.2552,40.2249],
  "Gümüşhane":[40.4386,39.5086], "Hakkari":[37.5744,43.7408], "Şırnak":[37.5164,42.4611],
  "Tunceli":[39.1079,39.5401], "Bingöl":[38.8855,40.4966], "Niğde":[37.9667,34.6833],
  "Nevşehir":[38.6939,34.6857], "Kırşehir":[39.1425,34.1709], "Karaman":[37.1759,33.2287],
  "Bilecik":[40.0567,30.0665], "Bartın":[41.6344,32.3375], "Karabük":[41.2061,32.6204],
  "Sinop":[42.0231,35.1531], "Çankırı":[40.6013,33.6134], "Uşak":[38.6823,29.4082],
  "Burdur":[37.7203,30.2906], "Kırıkkale":[39.8468,33.5153], "Erzincan":[39.7500,39.5000],
  "Adıyaman":[37.7648,38.2786], "Yalova":[40.6500,29.2667], "Kilis":[36.7184,37.1212],
};

// Flatten TR data into supplier records (district = "Merkez", a small lat/lon jitter
// keeps multiple suppliers from rendering on the exact same pixel).
function jitter(seed, scale) {
  const r = ((seed * 9301 + 49297) % 233280) / 233280;
  return (r - 0.5) * 2 * scale;
}
function hash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

const CURATED_SUPPLIERS = (() => {
  const out = [];
  for (const [city, list] of Object.entries(TR_SUPPLIERS_FLAT)) {
    const coords = TR_CITY_COORDS[city];
    if (!coords) continue;
    const [lat, lon] = coords;
    list.forEach(([name, phone], i) => {
      const seed = hash(`${city}-${i}-${name}`);
      out.push({
        country: "TR",
        city,
        district: "Merkez",
        name,
        phone,
        lat: lat + jitter(seed, 0.012),
        lon: lon + jitter(seed + 1, 0.012),
      });
    });
  }
  return out;
})();

// ─────────────────────────────────────────────────────────────
// CITY CENTROID COORDINATES (key format: `${ISO}-${cityName}`)
// ─────────────────────────────────────────────────────────────
const TR_COORDS_FOR_EXPORT = Object.fromEntries(
  Object.entries(TR_CITY_COORDS).map(([city, coords]) => [`TR-${city}`, coords])
);

export const CITY_COORDS = {
  ...TR_COORDS_FOR_EXPORT,
  // Germany
  "DE-Berlin":     [52.5200, 13.4050],  "DE-Munich":     [48.1351, 11.5820],
  "DE-Hamburg":    [53.5511,  9.9937],  "DE-Frankfurt":  [50.1109,  8.6821],
  "DE-Cologne":    [50.9375,  6.9603],  "DE-Stuttgart":  [48.7758,  9.1829],
  "DE-Düsseldorf": [51.2277,  6.7735],  "DE-Leipzig":    [51.3397, 12.3731],
  "DE-Dortmund":   [51.5136,  7.4653],  "DE-Bremen":     [53.0793,  8.8017],
  "DE-Dresden":    [51.0504, 13.7373],  "DE-Hannover":   [52.3759,  9.7320],
  "DE-Nuremberg":  [49.4521, 11.0767],  "DE-Essen":      [51.4556,  7.0116],
  // France
  "FR-Paris":[48.8566,2.3522], "FR-Marseille":[43.2965,5.3698], "FR-Lyon":[45.7640,4.8357],
  "FR-Toulouse":[43.6047,1.4442], "FR-Nice":[43.7102,7.2620], "FR-Nantes":[47.2184,-1.5536],
  "FR-Strasbourg":[48.5734,7.7521], "FR-Bordeaux":[44.8378,-0.5792], "FR-Lille":[50.6292,3.0573],
  "FR-Montpellier":[43.6108,3.8767], "FR-Rennes":[48.1173,-1.6778], "FR-Reims":[49.2583,4.0317],
  // United Kingdom
  "GB-London":[51.5074,-0.1278], "GB-Birmingham":[52.4862,-1.8904], "GB-Manchester":[53.4808,-2.2426],
  "GB-Glasgow":[55.8642,-4.2518], "GB-Liverpool":[53.4084,-2.9916], "GB-Leeds":[53.8008,-1.5491],
  "GB-Edinburgh":[55.9533,-3.1883], "GB-Bristol":[51.4545,-2.5879], "GB-Cardiff":[51.4816,-3.1791],
  "GB-Belfast":[54.5973,-5.9301], "GB-Newcastle":[54.9783,-1.6178], "GB-Sheffield":[53.3811,-1.4701],
  // Italy
  "IT-Rome":[41.9028,12.4964], "IT-Milan":[45.4642,9.1900], "IT-Naples":[40.8518,14.2681],
  "IT-Turin":[45.0703,7.6869], "IT-Palermo":[38.1157,13.3615], "IT-Genoa":[44.4056,8.9463],
  "IT-Bologna":[44.4949,11.3426], "IT-Florence":[43.7696,11.2558], "IT-Bari":[41.1171,16.8719],
  "IT-Catania":[37.5079,15.0830], "IT-Venice":[45.4408,12.3155], "IT-Verona":[45.4384,10.9916],
  // Spain
  "ES-Madrid":[40.4168,-3.7038], "ES-Barcelona":[41.3851,2.1734], "ES-Valencia":[39.4699,-0.3763],
  "ES-Seville":[37.3891,-5.9845], "ES-Zaragoza":[41.6488,-0.8891], "ES-Málaga":[36.7213,-4.4214],
  "ES-Murcia":[37.9922,-1.1307], "ES-Palma":[39.5696,2.6502], "ES-Bilbao":[43.2630,-2.9350],
  "ES-Alicante":[38.3452,-0.4810], "ES-Córdoba":[37.8882,-4.7794], "ES-Granada":[37.1773,-3.5986],
  // United States
  "US-New York":[40.7128,-74.0060], "US-Los Angeles":[34.0522,-118.2437], "US-Chicago":[41.8781,-87.6298],
  "US-Houston":[29.7604,-95.3698], "US-San Francisco":[37.7749,-122.4194], "US-Phoenix":[33.4484,-112.0740],
  "US-Philadelphia":[39.9526,-75.1652], "US-San Antonio":[29.4241,-98.4936], "US-San Diego":[32.7157,-117.1611],
  "US-Dallas":[32.7767,-96.7970], "US-San Jose":[37.3382,-121.8863], "US-Austin":[30.2672,-97.7431],
  "US-Jacksonville":[30.3322,-81.6557], "US-Fort Worth":[32.7555,-97.3308], "US-Columbus":[39.9612,-82.9988],
  "US-Charlotte":[35.2271,-80.8431], "US-Indianapolis":[39.7684,-86.1581], "US-Seattle":[47.6062,-122.3321],
  "US-Denver":[39.7392,-104.9903], "US-Washington":[38.9072,-77.0369], "US-Boston":[42.3601,-71.0589],
  "US-Miami":[25.7617,-80.1918],
  // Canada
  "CA-Toronto":[43.6532,-79.3832], "CA-Montreal":[45.5017,-73.5673], "CA-Vancouver":[49.2827,-123.1207],
  "CA-Calgary":[51.0447,-114.0719], "CA-Ottawa":[45.4215,-75.6972], "CA-Edmonton":[53.5461,-113.4938],
  "CA-Winnipeg":[49.8951,-97.1384], "CA-Quebec City":[46.8139,-71.2080], "CA-Hamilton":[43.2557,-79.8711],
  "CA-Halifax":[44.6488,-63.5752],
  // Mexico
  "MX-Mexico City":[19.4326,-99.1332], "MX-Guadalajara":[20.6597,-103.3496], "MX-Monterrey":[25.6866,-100.3161],
  "MX-Puebla":[19.0414,-98.2063], "MX-Tijuana":[32.5149,-117.0382], "MX-León":[21.1250,-101.6859],
  "MX-Juárez":[31.6904,-106.4245], "MX-Zapopan":[20.7235,-103.3925], "MX-Mérida":[20.9674,-89.5926],
  "MX-Cancún":[21.1619,-86.8515],
  // Brazil
  "BR-São Paulo":[-23.5505,-46.6333], "BR-Rio de Janeiro":[-22.9068,-43.1729], "BR-Brasília":[-15.7942,-47.8825],
  "BR-Salvador":[-12.9714,-38.5014], "BR-Fortaleza":[-3.7172,-38.5433], "BR-Belo Horizonte":[-19.9167,-43.9345],
  "BR-Manaus":[-3.1190,-60.0217], "BR-Curitiba":[-25.4284,-49.2733], "BR-Recife":[-8.0476,-34.8770],
  "BR-Porto Alegre":[-30.0346,-51.2177],
  // Argentina
  "AR-Buenos Aires":[-34.6037,-58.3816], "AR-Córdoba":[-31.4201,-64.1888], "AR-Rosario":[-32.9442,-60.6505],
  "AR-Mendoza":[-32.8895,-68.8458], "AR-La Plata":[-34.9215,-57.9545], "AR-Tucumán":[-26.8083,-65.2176],
  "AR-Mar del Plata":[-38.0055,-57.5426], "AR-Salta":[-24.7821,-65.4232],
  // Russia
  "RU-Moscow":[55.7558,37.6173], "RU-Saint Petersburg":[59.9311,30.3609], "RU-Novosibirsk":[55.0084,82.9357],
  "RU-Yekaterinburg":[56.8389,60.6057], "RU-Kazan":[55.7887,49.1221], "RU-Nizhny Novgorod":[56.2965,43.9361],
  "RU-Chelyabinsk":[55.1644,61.4368], "RU-Omsk":[54.9885,73.3242], "RU-Samara":[53.2415,50.2212],
  "RU-Rostov-on-Don":[47.2357,39.7015],
  // China
  "CN-Beijing":[39.9042,116.4074], "CN-Shanghai":[31.2304,121.4737], "CN-Guangzhou":[23.1291,113.2644],
  "CN-Shenzhen":[22.5431,114.0579], "CN-Chengdu":[30.5728,104.0668], "CN-Tianjin":[39.3434,117.3616],
  "CN-Wuhan":[30.5928,114.3055], "CN-Chongqing":[29.4316,106.9123], "CN-Xi'an":[34.3416,108.9398],
  "CN-Hangzhou":[30.2741,120.1551], "CN-Nanjing":[32.0603,118.7969], "CN-Suzhou":[31.2989,120.5853],
  // Japan
  "JP-Tokyo":[35.6762,139.6503], "JP-Osaka":[34.6937,135.5023], "JP-Yokohama":[35.4437,139.6380],
  "JP-Nagoya":[35.1815,136.9066], "JP-Sapporo":[43.0618,141.3545], "JP-Fukuoka":[33.5904,130.4017],
  "JP-Kobe":[34.6901,135.1955], "JP-Kyoto":[35.0116,135.7681], "JP-Kawasaki":[35.5308,139.7029],
  "JP-Saitama":[35.8617,139.6455], "JP-Hiroshima":[34.3853,132.4553], "JP-Sendai":[38.2682,140.8694],
  // South Korea
  "KR-Seoul":[37.5665,126.9780], "KR-Busan":[35.1796,129.0756], "KR-Incheon":[37.4563,126.7052],
  "KR-Daegu":[35.8714,128.6014], "KR-Daejeon":[36.3504,127.3845], "KR-Gwangju":[35.1595,126.8526],
  "KR-Suwon":[37.2636,127.0286], "KR-Ulsan":[35.5384,129.3114],
  // India
  "IN-Mumbai":[19.0760,72.8777], "IN-Delhi":[28.7041,77.1025], "IN-Bangalore":[12.9716,77.5946],
  "IN-Hyderabad":[17.3850,78.4867], "IN-Ahmedabad":[23.0225,72.5714], "IN-Chennai":[13.0827,80.2707],
  "IN-Kolkata":[22.5726,88.3639], "IN-Surat":[21.1702,72.8311], "IN-Pune":[18.5204,73.8567],
  "IN-Jaipur":[26.9124,75.7873], "IN-Lucknow":[26.8467,80.9462],
  // Australia
  "AU-Sydney":[-33.8688,151.2093], "AU-Melbourne":[-37.8136,144.9631], "AU-Brisbane":[-27.4698,153.0251],
  "AU-Perth":[-31.9505,115.8605], "AU-Adelaide":[-34.9285,138.6007], "AU-Gold Coast":[-28.0167,153.4000],
  "AU-Canberra":[-35.2809,149.1300], "AU-Newcastle":[-32.9283,151.7817], "AU-Hobart":[-42.8821,147.3272],
  // Netherlands
  "NL-Amsterdam":[52.3676,4.9041], "NL-Rotterdam":[51.9244,4.4777], "NL-The Hague":[52.0705,4.3007],
  "NL-Utrecht":[52.0907,5.1214], "NL-Eindhoven":[51.4416,5.4697], "NL-Groningen":[53.2194,6.5665],
  "NL-Tilburg":[51.5719,5.0672], "NL-Almere":[52.3508,5.2647],
  // Belgium
  "BE-Brussels":[50.8503,4.3517], "BE-Antwerp":[51.2194,4.4025], "BE-Ghent":[51.0543,3.7174],
  "BE-Charleroi":[50.4108,4.4446], "BE-Liège":[50.6326,5.5797], "BE-Bruges":[51.2093,3.2247],
  "BE-Namur":[50.4674,4.8718],
  // Switzerland
  "CH-Zürich":[47.3769,8.5417], "CH-Geneva":[46.2044,6.1432], "CH-Basel":[47.5596,7.5886],
  "CH-Bern":[46.9480,7.4474], "CH-Lausanne":[46.5197,6.6323], "CH-Winterthur":[47.5009,8.7240],
  "CH-Lucerne":[47.0502,8.3093], "CH-St. Gallen":[47.4245,9.3767],
  // Austria
  "AT-Vienna":[48.2082,16.3738], "AT-Graz":[47.0707,15.4395], "AT-Linz":[48.3069,14.2858],
  "AT-Salzburg":[47.8095,13.0550], "AT-Innsbruck":[47.2692,11.4041], "AT-Klagenfurt":[46.6247,14.3056],
  // Nordics
  "SE-Stockholm":[59.3293,18.0686], "SE-Gothenburg":[57.7089,11.9746], "SE-Malmö":[55.6049,13.0038],
  "SE-Uppsala":[59.8586,17.6389], "SE-Västerås":[59.6099,16.5448], "SE-Örebro":[59.2753,15.2134],
  "SE-Linköping":[58.4108,15.6214],
  "NO-Oslo":[59.9139,10.7522], "NO-Bergen":[60.3913,5.3221], "NO-Stavanger":[58.9700,5.7331],
  "NO-Trondheim":[63.4305,10.3951], "NO-Drammen":[59.7440,10.2045], "NO-Tromsø":[69.6492,18.9553],
  "DK-Copenhagen":[55.6761,12.5683], "DK-Aarhus":[56.1629,10.2039], "DK-Odense":[55.4038,10.4024],
  "DK-Aalborg":[57.0488,9.9217], "DK-Esbjerg":[55.4765,8.4594],
  "FI-Helsinki":[60.1699,24.9384], "FI-Espoo":[60.2055,24.6559], "FI-Tampere":[61.4978,23.7610],
  "FI-Vantaa":[60.2934,25.0378], "FI-Turku":[60.4518,22.2666], "FI-Oulu":[65.0121,25.4651],
  // East EU
  "PL-Warsaw":[52.2297,21.0122], "PL-Kraków":[50.0647,19.9450], "PL-Łódź":[51.7592,19.4560],
  "PL-Wrocław":[51.1079,17.0385], "PL-Poznań":[52.4064,16.9252], "PL-Gdańsk":[54.3520,18.6466],
  "PL-Szczecin":[53.4285,14.5528], "PL-Bydgoszcz":[53.1235,18.0084], "PL-Lublin":[51.2465,22.5684],
  "CZ-Prague":[50.0755,14.4378], "CZ-Brno":[49.1951,16.6068], "CZ-Ostrava":[49.8209,18.2625],
  "CZ-Plzeň":[49.7384,13.3736], "CZ-Liberec":[50.7663,15.0543], "CZ-Olomouc":[49.5938,17.2509],
  "HU-Budapest":[47.4979,19.0402], "HU-Debrecen":[47.5316,21.6273], "HU-Szeged":[46.2530,20.1414],
  "HU-Miskolc":[48.1035,20.7784], "HU-Pécs":[46.0727,18.2323], "HU-Győr":[47.6849,17.6350],
  "GR-Athens":[37.9838,23.7275], "GR-Thessaloniki":[40.6401,22.9444], "GR-Patras":[38.2466,21.7346],
  "GR-Piraeus":[37.9420,23.6469], "GR-Larissa":[39.6390,22.4191], "GR-Heraklion":[35.3387,25.1442],
  "GR-Volos":[39.3681,22.9426],
  "PT-Lisbon":[38.7223,-9.1393], "PT-Porto":[41.1579,-8.6291], "PT-Vila Nova de Gaia":[41.1239,-8.6118],
  "PT-Amadora":[38.7536,-9.2310], "PT-Braga":[41.5454,-8.4265], "PT-Funchal":[32.6669,-16.9241],
  "PT-Coimbra":[40.2033,-8.4103],
  "IE-Dublin":[53.3498,-6.2603], "IE-Cork":[51.8985,-8.4756], "IE-Limerick":[52.6680,-8.6305],
  "IE-Galway":[53.2707,-9.0568], "IE-Waterford":[52.2593,-7.1101], "IE-Drogheda":[53.7188,-6.3478],
  "RO-Bucharest":[44.4268,26.1025], "RO-Cluj-Napoca":[46.7712,23.6236], "RO-Timișoara":[45.7489,21.2087],
  "RO-Iași":[47.1585,27.6014], "RO-Constanța":[44.1598,28.6348], "RO-Craiova":[44.3302,23.7949],
  "RO-Brașov":[45.6427,25.5887],
  "BG-Sofia":[42.6977,23.3219], "BG-Plovdiv":[42.1354,24.7453], "BG-Varna":[43.2141,27.9147],
  "BG-Burgas":[42.5048,27.4626], "BG-Ruse":[43.8564,25.9709], "BG-Stara Zagora":[42.4258,25.6345],
  "UA-Kyiv":[50.4501,30.5234], "UA-Kharkiv":[49.9935,36.2304], "UA-Odesa":[46.4825,30.7233],
  "UA-Dnipro":[48.4647,35.0462], "UA-Donetsk":[48.0159,37.8028], "UA-Zaporizhzhia":[47.8388,35.1396],
  "UA-Lviv":[49.8397,24.0297],
  // Middle East
  "IL-Jerusalem":[31.7683,35.2137], "IL-Tel Aviv":[32.0853,34.7818], "IL-Haifa":[32.7940,34.9896],
  "IL-Rishon LeZion":[31.9730,34.7925], "IL-Petah Tikva":[32.0878,34.8878], "IL-Ashdod":[31.8014,34.6435],
  "IL-Netanya":[32.3215,34.8532],
  "AE-Dubai":[25.2048,55.2708], "AE-Abu Dhabi":[24.4539,54.3773], "AE-Sharjah":[25.3463,55.4209],
  "AE-Ajman":[25.4111,55.4351], "AE-Al Ain":[24.2075,55.7447], "AE-Ras al-Khaimah":[25.7895,55.9432],
  "AE-Fujairah":[25.1288,56.3265],
  "SA-Riyadh":[24.7136,46.6753], "SA-Jeddah":[21.4858,39.1925], "SA-Mecca":[21.3891,39.8579],
  "SA-Medina":[24.5247,39.5692], "SA-Dammam":[26.3927,49.9777], "SA-Khobar":[26.2172,50.1971],
  "SA-Tabuk":[28.3998,36.5700],
  "QA-Doha":[25.2854,51.5310], "QA-Al Rayyan":[25.2919,51.4244], "QA-Al Wakrah":[25.1715,51.6034],
  "QA-Lusail":[25.4318,51.4869],
  // Africa
  "EG-Cairo":[30.0444,31.2357], "EG-Alexandria":[31.2001,29.9187], "EG-Giza":[30.0131,31.2089],
  "EG-Shubra El Kheima":[30.1286,31.2422], "EG-Port Said":[31.2653,32.3019], "EG-Suez":[29.9668,32.5498],
  "EG-Luxor":[25.6872,32.6396],
  "ZA-Johannesburg":[-26.2041,28.0473], "ZA-Cape Town":[-33.9249,18.4241], "ZA-Durban":[-29.8587,31.0218],
  "ZA-Pretoria":[-25.7479,28.2293], "ZA-Port Elizabeth":[-33.9608,25.6022], "ZA-Bloemfontein":[-29.0852,26.1596],
  "NG-Lagos":[6.5244,3.3792], "NG-Abuja":[9.0765,7.3986], "NG-Kano":[12.0022,8.5919],
  "NG-Ibadan":[7.3775,3.9470], "NG-Port Harcourt":[4.8156,7.0498], "NG-Benin City":[6.3350,5.6037],
  "KE-Nairobi":[-1.2921,36.8219], "KE-Mombasa":[-4.0435,39.6682], "KE-Kisumu":[-0.0917,34.7680],
  "KE-Nakuru":[-0.3031,36.0800], "KE-Eldoret":[0.5143,35.2698],
  "MA-Casablanca":[33.5731,-7.5898], "MA-Rabat":[34.0209,-6.8416], "MA-Marrakesh":[31.6295,-7.9811],
  "MA-Fez":[34.0181,-5.0078], "MA-Tangier":[35.7595,-5.8340], "MA-Agadir":[30.4278,-9.5981],
  // SE Asia + Oceania
  "TH-Bangkok":[13.7563,100.5018], "TH-Chiang Mai":[18.7883,98.9853], "TH-Pattaya":[12.9236,100.8825],
  "TH-Phuket":[7.8804,98.3923], "TH-Nonthaburi":[13.8622,100.5135],
  "VN-Hanoi":[21.0285,105.8542], "VN-Ho Chi Minh City":[10.8231,106.6297], "VN-Da Nang":[16.0544,108.2022],
  "VN-Haiphong":[20.8449,106.6881], "VN-Can Tho":[10.0452,105.7469],
  "ID-Jakarta":[-6.2088,106.8456], "ID-Surabaya":[-7.2575,112.7521], "ID-Bandung":[-6.9175,107.6191],
  "ID-Medan":[3.5952,98.6722], "ID-Semarang":[-6.9667,110.4167], "ID-Makassar":[-5.1477,119.4327],
  "ID-Yogyakarta":[-7.7956,110.3695],
  "MY-Kuala Lumpur":[3.1390,101.6869], "MY-George Town":[5.4145,100.3294], "MY-Ipoh":[4.5975,101.0901],
  "MY-Johor Bahru":[1.4927,103.7414], "MY-Shah Alam":[3.0733,101.5185], "MY-Petaling Jaya":[3.1073,101.6067],
  "SG-Singapore":[1.3521,103.8198],
  "PH-Manila":[14.5995,120.9842], "PH-Quezon City":[14.6760,121.0437], "PH-Davao":[7.1907,125.4553],
  "PH-Cebu City":[10.3157,123.8854], "PH-Makati":[14.5547,121.0244],
  "NZ-Auckland":[-36.8485,174.7633], "NZ-Wellington":[-41.2865,174.7762], "NZ-Christchurch":[-43.5321,172.6362],
  "NZ-Hamilton":[-37.7870,175.2793], "NZ-Tauranga":[-37.6878,176.1651], "NZ-Dunedin":[-45.8788,170.5028],
  // Caucasus / Central Asia / South Asia
  "AZ-Baku":[40.4093,49.8671], "AZ-Ganja":[40.6828,46.3606], "AZ-Sumqayit":[40.5894,49.6691],
  "AZ-Mingachevir":[40.7700,47.0500],
  "KZ-Almaty":[43.2220,76.8512], "KZ-Astana":[51.1605,71.4704], "KZ-Shymkent":[42.3417,69.5901],
  "KZ-Karaganda":[49.8047,73.1094], "KZ-Aktobe":[50.2839,57.1670],
  "GE-Tbilisi":[41.7151,44.8271], "GE-Batumi":[41.6168,41.6367], "GE-Kutaisi":[42.2679,42.7180],
  "GE-Rustavi":[41.5494,45.0079],
  "IR-Tehran":[35.6892,51.3890], "IR-Mashhad":[36.2605,59.6168], "IR-Isfahan":[32.6546,51.6680],
  "IR-Karaj":[35.8327,50.9916], "IR-Shiraz":[29.5926,52.5836], "IR-Tabriz":[38.0962,46.2738],
  "PK-Karachi":[24.8607,67.0011], "PK-Lahore":[31.5204,74.3587], "PK-Faisalabad":[31.4504,73.1350],
  "PK-Rawalpindi":[33.5651,73.0169], "PK-Islamabad":[33.6844,73.0479], "PK-Multan":[30.1575,71.5249],
};

// ─────────────────────────────────────────────────────────────
// GLOBAL AUTO-GENERATOR — 3 suppliers per non-TR city
// ─────────────────────────────────────────────────────────────
const COUNTRY_DIAL = {
  TR: "+90", DE: "+49", FR: "+33", GB: "+44", IT: "+39", ES: "+34",
  US: "+1",  CA: "+1",  MX: "+52", BR: "+55", AR: "+54", RU: "+7",
  CN: "+86", JP: "+81", KR: "+82", IN: "+91", AU: "+61", NL: "+31",
  BE: "+32", CH: "+41", AT: "+43", SE: "+46", NO: "+47", DK: "+45",
  FI: "+358",PL: "+48", GR: "+30", PT: "+351",IE: "+353",CZ: "+420",
  HU: "+36", RO: "+40", BG: "+359",UA: "+380",IL: "+972",AE: "+971",
  SA: "+966",QA: "+974",EG: "+20", ZA: "+27", NG: "+234",KE: "+254",
  MA: "+212",TH: "+66", VN: "+84", ID: "+62", MY: "+60", SG: "+65",
  PH: "+63", NZ: "+64", AZ: "+994",KZ: "+7",  GE: "+995",IR: "+98",
  PK: "+92",
};

const BRAND_POOL = [
  "Rockwool",            "Knauf Insulation",      "Saint-Gobain Isover",
  "Owens Corning",       "Kingspan Insulation",   "BASF Polymers",
  "DOW Building",        "Sika Thermal",          "Paroc Group",
  "URSA Insulation",     "Bauder Roofing",        "Recticel Insulation",
  "Soprema Thermal",     "Atlas Roofing",         "Johns Manville",
  "CertainTeed Building","Carlisle SynTec",       "Henkel Construction",
  "Mapei Thermal",       "Sto Façade",            "Caparol Insulation",
  "Wienerberger Brick",  "Heraklith Boards",      "Steico Wood Fibre",
  "Thermafiber Mineral", "Pittsburgh Corning",    "Cabot Aerogel",
];

function makePhone(dial, seed) {
  const a = String(200 + (seed % 800)).padStart(3, "0");
  const b = String(100 + ((seed >> 3) % 900)).padStart(3, "0");
  const c = String(1000 + ((seed >> 7) % 9000)).padStart(4, "0");
  return `${dial} ${a} ${b} ${c}`;
}

const AUTO_SUPPLIERS = (() => {
  const out = [];
  for (const key of Object.keys(CITY_COORDS)) {
    if (key.startsWith("TR-")) continue; // TR fully curated above
    const dashIdx = key.indexOf("-");
    const country = key.slice(0, dashIdx);
    const city = key.slice(dashIdx + 1);
    const [clat, clon] = CITY_COORDS[key];
    const dial = COUNTRY_DIAL[country] || "+1";
    const cityHash = hash(city);
    for (let i = 0; i < 3; i++) {
      const brandIdx = (cityHash + i * 7) % BRAND_POOL.length;
      const brand = BRAND_POOL[brandIdx];
      const district = i === 0 ? "Central" : i === 1 ? "North" : "South";
      out.push({
        country,
        city,
        district,
        name: `${brand} · ${city}`,
        phone: makePhone(dial, cityHash + i * 131),
        lat: clat + jitter(cityHash + i * 17, 0.015),
        lon: clon + jitter(cityHash + i * 23, 0.015),
      });
    }
  }
  return out;
})();

export const SUPPLIERS = [...CURATED_SUPPLIERS, ...AUTO_SUPPLIERS];

// ─────────────────────────────────────────────────────────────
// DISTRICT MAP — one entry per (country, city, district) tuple
// ─────────────────────────────────────────────────────────────
const __districtMap = (() => {
  const m = new Map();
  for (const s of SUPPLIERS) {
    const key = `${s.country}-${s.city}-${s.district}`;
    if (!m.has(key)) m.set(key, { country: s.country, city: s.city, name: s.district, lat: s.lat, lon: s.lon });
  }
  return m;
})();

// ─────────────────────────────────────────────────────────────
// PUBLIC HELPERS
// ─────────────────────────────────────────────────────────────
export function getSuppliersInCity(countryCode, cityName) {
  return SUPPLIERS.filter(s => s.country === countryCode && s.city === cityName);
}

export function getSuppliersInDistrict(countryCode, cityName, districtName) {
  return SUPPLIERS.filter(s =>
    s.country === countryCode && s.city === cityName && s.district === districtName
  );
}

export function getDistrictsForCity(countryCode, cityName) {
  return Array.from(__districtMap.values())
    .filter(d => d.country === countryCode && d.city === cityName);
}

export function getCitiesWithCoords(countryCode, cityList) {
  const fromCoords = Object.keys(CITY_COORDS)
    .filter((key) => key.startsWith(`${countryCode}-`))
    .map((key) => {
      const name = key.slice(countryCode.length + 1);
      const [lat, lon] = CITY_COORDS[key];
      const hasSuppliers = SUPPLIERS.some(s => s.country === countryCode && s.city === name);
      return { name, lat, lon, hasSuppliers };
    });
  const known = new Set(fromCoords.map((c) => c.name));
  const extras = (cityList || [])
    .filter((n) => !known.has(n))
    .map((n) => {
      const key = `${countryCode}-${n}`;
      if (!CITY_COORDS[key]) return null;
      const [lat, lon] = CITY_COORDS[key];
      return { name: n, lat, lon, hasSuppliers: false };
    })
    .filter(Boolean);
  return [...fromCoords, ...extras];
}

export { COUNTRY_CITIES };
